// GET /api/transactions/{transaction_id} -- the same TransactionRow plus
// `revisions[]`, the full amendment chain walked oldest-first through
// `supersedes_transaction_id` (M1 plan §5.1 point 5). This is the data
// source for `AmendmentTrail`.
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import { canonicalTransactionRowSchema, removalReasonSchema, transactionRowSchema } from './useTransactions';
import { annotationOptionSchema } from './useReconciliation';
import type { ReadScope } from './useReadScope';

export const transactionRevisionSchema = z.object({
  transaction_id: z.string(),
  revision: z.number(),
  amount: z.string(),
  merchant_raw: z.string(),
  posted_at: z.string(),
  amended_at: z.string().nullable(),
  removal_reason: removalReasonSchema.nullable(),
});

export const transactionDetailSchema = transactionRowSchema.extend({
  revisions: z.array(transactionRevisionSchema),
});
const strictTransactionDetailSchema = transactionDetailSchema.strict();

export type TransactionRevision = z.infer<typeof transactionRevisionSchema>;
export type TransactionDetail = z.infer<typeof transactionDetailSchema>;

const uuidSchema = z.string().uuid();
const canonicalDateTimeSchema = z.string().datetime({ offset: true });
const canonicalTransactionRowDetailSchema = canonicalTransactionRowSchema.extend({
  transaction_id: uuidSchema,
  posted_at: canonicalDateTimeSchema,
  authorized_at: canonicalDateTimeSchema.nullable(),
  revision: z.number().int().nonnegative(),
  superseded_at: canonicalDateTimeSchema.nullable(),
}).strict();
const canonicalRevisionSchema = transactionRevisionSchema.extend({
  transaction_id: uuidSchema,
  revision: z.number().int().nonnegative(),
  posted_at: canonicalDateTimeSchema,
  amended_at: canonicalDateTimeSchema.nullable(),
}).strict();
const physicalSourceSchema = z.object({
  transaction_id: uuidSchema,
  account_id: uuidSchema,
  item_id: uuidSchema.nullable(),
  revision: z.number().int().nonnegative(),
  superseded_at: z.string().datetime({ offset: true }).nullable(),
  removal_reason: removalReasonSchema.nullable(),
  is_representative: z.boolean(),
}).strict();

export const canonicalTransactionDetailSchema = canonicalTransactionRowDetailSchema.extend({
  revisions: z.array(canonicalRevisionSchema),
  physical_sources: z.array(physicalSourceSchema),
  annotation_options: z.array(annotationOptionSchema),
}).strict();

const primaryCanonicalMetaSchema = z.union([
  z.object({}).strict(),
  z.object({ alias_of: uuidSchema }).strict(),
]);
const primaryInitializationMetaSchema = z.object({ reconciliation_initialization_required: z.literal(true) }).strict();
const diagnosticMetaSchema = z.object({ diagnostic_scope: z.literal(true) }).strict();

type PrimaryCanonicalTransactionDetail = z.infer<typeof canonicalTransactionDetailSchema> & {
  requestedTransactionId: string;
  primaryKind: 'canonical' | 'canonical_alias';
  aliasOf?: string;
};
type PrimaryInitializationTransactionDetail = TransactionDetail & {
  requestedTransactionId: string;
  primaryKind: 'initialization_required_physical';
  aliasOf?: never;
};
export type PrimaryTransactionDetail = PrimaryCanonicalTransactionDetail | PrimaryInitializationTransactionDetail;

export type ParsedPrimaryTransactionDetail = { data: PrimaryTransactionDetail; meta: z.infer<typeof primaryCanonicalMetaSchema> | z.infer<typeof primaryInitializationMetaSchema> };
export type ParsedDiagnosticTransactionDetail = { data: TransactionDetail; meta: z.infer<typeof diagnosticMetaSchema> };

export function parseTransactionDetailResponse<T extends ReadScope | null>(payload: unknown, requestedTransactionId: string, scope: T): T extends null ? ParsedPrimaryTransactionDetail : ParsedDiagnosticTransactionDetail {
  if (scope !== null) {
    const parsed = parseEnvelope(strictTransactionDetailSchema, payload);
    diagnosticMetaSchema.parse(parsed.meta);
    return { data: parsed.data, meta: parsed.meta } as unknown as T extends null ? ParsedPrimaryTransactionDetail : ParsedDiagnosticTransactionDetail;
  }

  const parsed = parseEnvelope(z.unknown(), payload);
  const canonicalMeta = primaryCanonicalMetaSchema.safeParse(parsed.meta);
  if (canonicalMeta.success) {
    const data = canonicalTransactionDetailSchema.parse(parsed.data);
    const aliasOf = 'alias_of' in canonicalMeta.data ? canonicalMeta.data.alias_of : undefined;
    if (aliasOf !== undefined && aliasOf !== data.canonical_transaction_id) {
      throw new Error('primary transaction alias must equal canonical_transaction_id');
    }
    return {
      data: {
        ...data,
        requestedTransactionId,
        primaryKind: aliasOf === undefined ? 'canonical' : 'canonical_alias',
        ...(aliasOf === undefined ? {} : { aliasOf }),
      } satisfies PrimaryTransactionDetail,
      meta: canonicalMeta.data,
    } as unknown as T extends null ? ParsedPrimaryTransactionDetail : ParsedDiagnosticTransactionDetail;
  }

  const initializationMeta = primaryInitializationMetaSchema.parse(parsed.meta);
  const data = strictTransactionDetailSchema.parse(parsed.data);
  return {
    data: {
      ...data,
      requestedTransactionId,
      primaryKind: 'initialization_required_physical',
    } satisfies PrimaryTransactionDetail,
    meta: initializationMeta,
  } as unknown as T extends null ? ParsedPrimaryTransactionDetail : ParsedDiagnosticTransactionDetail;
}

export function useTransaction(transactionId: string | undefined, scope: ReadScope | null = 'current', enabled = true) {
  const requestScope = scope === undefined ? 'current' : scope;
  return useQuery({
    queryKey: ['transactions', 'detail', transactionId, { scope: requestScope }],
    enabled: Boolean(transactionId) && enabled,
    queryFn: async () => {
      const params = requestScope === null ? {} : { scope: requestScope };
      const response = await client.get(`/api/transactions/${transactionId}`, { params });
      if (requestScope === null) return parseTransactionDetailResponse(response.data, transactionId as string, null).data;
      return parseTransactionDetailResponse(response.data, transactionId as string, requestScope).data;
    },
  });
}
